import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDrag } from 'react-dnd'
import AdderButton from './AdderButton';
import DeleteButton from './DeleteButton';

const PROPOSAL_TABLE_ROW_TYPE = 'proposal-table-row'

const ControlRow = ({
  controlRowsClicked,
  isControlRowClicked,
  setControlRowsClicked,
  parentRowId,
  isFirstElement,
  isLastElement,
  isFromHeader = true,
  parentRowType,
  isControlRowAfterClicked,
  setParentLineHovered,
  zIndex,
  columnLength,
  boxId,
  placementIndex,
  parentKey,
  headerTopOffset = 0,
  moveRow,
  rowElementRef,
  onControlRowClick,
  columnWidthPercent,
  // Groupe de lignes fusionnées (cf. Cell) : hauteur mesurée et numéros.
  spanHeight = null,
  rowLabel,
}) => {
  const borderLeftTable = isFirstElement ? 2 : 1
  const borderRightTable = isLastElement ? 2 : 1
  const ref = useRef()
  const canDragDrop = !isFromHeader && typeof moveRow === 'function'

  const [{ isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: PROPOSAL_TABLE_ROW_TYPE,
      item: { id: parentRowId },
      collect: (monitor) => ({
        isDragging: monitor.getItem()?.id === parentRowId && monitor.isDragging(),
      }),
      end: () => {
        document.querySelectorAll('[data-drag-row-cell].row-line-hovered').forEach(el => {
          el.classList.remove('row-line-hovered', 'is-first-row')
        })
      },
      canDrag: canDragDrop,
    }),
    [parentRowId, canDragDrop]
  )

  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    if (isDragging) {
      setControlRowsClicked([])
      document.querySelectorAll('[data-drag-row-cell].table-border-hovered')
        .forEach(el => el.classList.remove('table-border-hovered'))
      setIsHovered(false)
    }
  }, [isDragging, setControlRowsClicked])

  useEffect(() => {
    if (!isDragging) return

    const findScrollableAncestor = (element) => {
      let current = element?.parentElement
      while (current && current !== document.documentElement) {
        const { overflow, overflowY } = window.getComputedStyle(current)
        if (/(auto|scroll)/.test(overflow + overflowY) && current.scrollHeight > current.clientHeight) {
          return current
        }
        current = current.parentElement
      }
      return null
    }

    const scrollContainer = findScrollableAncestor(ref.current)
    if (!scrollContainer) return

    const EDGE_SIZE = 80
    const MAX_SPEED = 15
    let scrollSpeed = 0
    let rafId = null

    const tick = () => {
      if (scrollSpeed !== 0) {
        scrollContainer.scrollTop += scrollSpeed
      }
      rafId = requestAnimationFrame(tick)
    }

    const handleDragOver = (e) => {
      const rect = scrollContainer.getBoundingClientRect()
      const y = e.clientY

      if (y < rect.top + EDGE_SIZE) {
        const ratio = (rect.top + EDGE_SIZE - y) / EDGE_SIZE
        scrollSpeed = -Math.ceil(ratio * MAX_SPEED)
      } else if (y > rect.bottom - EDGE_SIZE) {
        const ratio = (y - (rect.bottom - EDGE_SIZE)) / EDGE_SIZE
        scrollSpeed = Math.ceil(ratio * MAX_SPEED)
      } else {
        scrollSpeed = 0
      }
    }

    document.addEventListener('dragover', handleDragOver)
    rafId = requestAnimationFrame(tick)

    return () => {
      document.removeEventListener('dragover', handleDragOver)
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [isDragging])

  useEffect(() => {
    if (!canDragDrop) return
    if (rowElementRef?.current) {
      previewRef(rowElementRef.current, { captureDraggingState: true })
    }
  }, [canDragDrop, previewRef, rowElementRef])

  const setRefs = useMemo(() => {
    if (!canDragDrop) return ref
    return (el) => {
      ref.current = el
      dragRef(el)
    }
  }, [canDragDrop, dragRef])

  const wrapperStyle = useMemo(() => {
    const firstBasePosition = isFromHeader ? "top" : "left"
    const secondBasePosition = isFromHeader ? "left" : "top"
    const sizedValue = isFromHeader ? "height" : "width"
    const fullValue = isFromHeader ? "width" : "height"
    const position = !isFromHeader ? "fixed" : "absolute"

    // Calcul des offsets
    let topOffset = -19;
    if (isFromHeader) {
      // Colonne enfant: remonter au-dessus du premier tr
      topOffset = -20 - headerTopOffset;
    }

    return {
      position,
      [`${sizedValue}`]: 18,
      [`${firstBasePosition}`]: topOffset,
      [`${secondBasePosition}`]: -borderLeftTable,
      [`${fullValue}`]: spanHeight != null
        ? `${spanHeight + borderLeftTable + borderRightTable}px`
        : `calc(100% + ${borderLeftTable}px + ${borderRightTable}px)`,
      zIndex: zIndex ?? 101
    }
  }, [borderLeftTable, borderRightTable, isFromHeader, zIndex, headerTopOffset, spanHeight])

  const rowBorders = useMemo(() => {
    const borderBase = `solid black`
    const fullBorder = isFromHeader ? "borderTop" : "borderLeft"
    const firstHalfBorder = isFromHeader ? "borderRight" : "borderBottom"
    const firstRadiusBorder = isFromHeader ? "borderTopRightRadius" : "borderBottomLeftRadius"
    const secondHalfBorder = isFromHeader ? "borderLeft" : "borderTop"
    const hiddenBorder = isFromHeader ? "borderBottom" : "borderRight"

    return {
      [`${fullBorder}`]: `1px ${borderBase}`,
      [`${firstHalfBorder}`]: `${borderRightTable / 2}px ${borderBase}`,
      [`${firstRadiusBorder}`]: isLastElement ? 4 : 0,
      [`${secondHalfBorder}`]: `${borderLeftTable / 2}px ${borderBase}`,
      borderTopLeftRadius: isFirstElement ? 4 : 0,
      [`${hiddenBorder}`]: 'none',
    }
  }, [isFromHeader, borderRightTable, isLastElement, borderLeftTable, isFirstElement])

  const isSingleHeaderSelection =
    parentRowType === 'header' &&
    Array.isArray(controlRowsClicked) &&
    controlRowsClicked.length === 1 &&
    isControlRowClicked

  const [isEditingPercent, setIsEditingPercent] = useState(false)
  const percentInputRef = useRef(null)
  const [editedPercent, setEditedPercent] = useState(
    columnWidthPercent != null
      ? `${Number(columnWidthPercent).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`
      : ''
  )

  useEffect(() => {
    if (!isFromHeader) return
    if (!isEditingPercent && columnWidthPercent != null) {
      setEditedPercent(
        `${Number(columnWidthPercent).toLocaleString('fr-FR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`
      )
    }
  }, [columnWidthPercent, isEditingPercent, isFromHeader])

  useEffect(() => {
    if (!isEditingPercent) return
    const el = percentInputRef.current
    if (!el) return
    const value = el.value ?? ''
    const pos = Math.max(0, value.length - 1) // juste avant le '%'
    try {
      el.setSelectionRange(pos, pos)
    } catch (e) {
      // ignore si non supporté
    }
  }, [isEditingPercent])

  const commitPercentChange = () => {
    if (!isFromHeader) return
    const normalized = editedPercent.replace(/\s/g, '').replace('%', '').replace(',', '.')
    const value = Number(normalized)
    if (Number.isNaN(value) || !Number.isFinite(value)) {
      setIsEditingPercent(false)
      return
    }
    const clamped = Math.max(1, Math.min(99, value))
    if (onControlRowClick) {
      onControlRowClick({
        parentRowId,
        parentRowType,
        shiftKey: false,
        type: 'setWidthPercent',
        widthPercent: clamped,
      })
    }
    setIsEditingPercent(false)
  }

  const rowStyle = useMemo(() => {
    const basePosition = isFromHeader ? "bottom" : "right"
    const fullValue = isFromHeader ? "width" : "height"
    const sizedValue = isFromHeader ? "height" : "width"

    return {
      backgroundColor: "#efefef",
      position: 'absolute',
      [`${fullValue}`]: "100%",
      [`${sizedValue}`]: 10,
      [`${basePosition}`]: 0,
      ...rowBorders
    }
  }, [isFromHeader, rowBorders])

  const handleOnMouseOver = () => {
    setIsHovered(true)
    const attr = isFromHeader ? 'data-col-cell' : 'data-drag-row-cell'
    document.querySelectorAll(`[${attr}="${parentRowId}"]`)
      .forEach(el => el.classList.add('table-border-hovered'))
  }

  const handleOnMouseLeave = () => {
    setIsHovered(false)
    const attr = isFromHeader ? 'data-col-cell' : 'data-drag-row-cell'
    document.querySelectorAll(`[${attr}="${parentRowId}"]`)
      .forEach(el => el.classList.remove('table-border-hovered'))
  }

  const handleOnClick = (event) => {
    // Perdre le focus éventuel d'une cellule ou d'un header quand on sélectionne
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur()
    }
    if (window.getSelection) {
      const selection = window.getSelection()
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges()
      }
    }

    const shiftKey = !!event?.shiftKey

    if (onControlRowClick) {
      onControlRowClick({
        parentRowId,
        parentRowType,
        shiftKey,
      })
      return
    }

    setControlRowsClicked((prev) => {
      const isOtherRowType = prev.some(row => row.parentRowType !== parentRowType)
      if (isOtherRowType) {
        return [{ parentRowId, parentRowType }]
      }
      const exists = prev.some(row => row.parentRowId === parentRowId && row.parentRowType === parentRowType)
      if (exists) {
        return prev.filter(row => !(row.parentRowId === parentRowId && row.parentRowType === parentRowType))
      } else {
        return [...prev, { parentRowId, parentRowType }]
      }
    })
  }

  return (
    <div style={wrapperStyle} className={`control-row-wrapper${isHovered ? ' cr-hovered' : ''}`}>
      {!isControlRowClicked && !isControlRowAfterClicked && <>
        {isFirstElement && <AdderButton
          isLeftButton
          isFromHeader={isFromHeader}
          isControlRowHovered={isHovered}
          setParentLineHovered={setParentLineHovered}
          columnLength={columnLength}
          boxId={boxId}
          placementIndex={0}
          rowElementRef={rowElementRef}
        />}
        <AdderButton
          isLeftButton={false}
          isFromHeader={isFromHeader}
          isControlRowHovered={isHovered}
          setParentLineHovered={setParentLineHovered}
          columnLength={columnLength}
          boxId={boxId}
          placementIndex={placementIndex}
          rowElementRef={rowElementRef}
        />
      </>
      }
      <div style={{
        width: "100%",
        height: "100%",
        pointerEvents: 'auto',
        opacity: isDragging ? 0.8 : 1,
      }}
        onMouseEnter={handleOnMouseOver}
        onMouseLeave={handleOnMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
          handleOnClick(e);
        }}
        ref={canDragDrop ? setRefs : ref}
      >
        {isControlRowClicked && !(isFirstElement && isLastElement) && <DeleteButton
          isFromHeader={isFromHeader}
          controlRowsClicked={controlRowsClicked}
          setControlRowsClicked={setControlRowsClicked}
          boxId={boxId}
          parentKey={parentKey}
          parentRowId={parentRowId}
          parentRowType={parentRowType}
        />}
        <div style={{
          ...rowStyle,
          cursor: canDragDrop
            ? (isDragging ? 'grabbing' : 'grab')
            : (isFromHeader && isControlRowClicked ? 'text' : 'pointer'),
        }} className={`${isHovered || isControlRowClicked ? "control-row-hovered" : ""}`}>
          {!isFromHeader && <div style={{
            fontSize: 10,
            fontFamily: 'initial',
            fontWeight: 400,
            fontStyle: 'normal',
            textDecoration: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            height: "100%"
          }}>{(rowLabel ?? placementIndex.toString())?.split('').map((ch) => <div>{ch}</div>)}</div>}
          {isFromHeader && columnWidthPercent != null && (
            <div
              style={{
                fontSize: 10,
                fontFamily: 'initial',
                fontWeight: 400,
                fontStyle: 'normal',
                textDecoration: 'none',
                color: 'rgb(75, 88, 95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                height: '100%',
                cursor: isSingleHeaderSelection ? 'text' : 'pointer',
              }}
              onClick={(e) => {
                if (!isSingleHeaderSelection) {
                  // Laisser la gestion de sélection standard (ne pas stopper la propagation)
                  return
                }

                e.stopPropagation()
                setIsEditingPercent(true)
              }}
            >
              {isEditingPercent ? (
                <input
                  ref={percentInputRef}
                  value={editedPercent}
                  onChange={(e) => setEditedPercent(e.target.value)}
                  onBlur={commitPercentChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      commitPercentChange()
                    } else if (e.key === 'Escape') {
                      setIsEditingPercent(false)
                    }
                  }}
                  autoFocus
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontSize: 10,
                    fontFamily: 'inherit',
                    width: '100%',
                  }}
                />
              ) : (
                <>
                  {Number(columnWidthPercent).toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  %
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ControlRow
